import { lazy, Suspense } from 'react';
import { useRoutes, Outlet, Navigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import MainLayout from '../components/admin/mainLayout/MainLayout';
import OrderMainLayout from '../components/order/mainLayout/MainLayout';
import RxMainLayout from '../components/rx/mainLayout/MainLayout';
import Error404Page from '../pages/common/Error404Page';
import Login from '../components/admin/auth/login';
import Loader from '../components/common/Loader';

const Dashboard = lazy(() => import('../pages/admin/dashboard'));
const Invite = lazy(() => import('../pages/questions/invite'));
const Home = lazy(() => import('../pages/questions/home'));
const Templates = lazy(() => import('../pages/admin/templates'));
const TemplateForm = lazy(() => import('../pages/admin/templates/TemplateForm'));
const TemplateFormPreviewPage = lazy(() => import('../pages/admin/templates/TemplateFormPreviewPage'));
const Users = lazy(() => import('../pages/admin/users'));
const AddEditUser = lazy(() => import('../pages/admin/users/AddEditUser'));
const BulkUploadUsers = lazy(() => import('../pages/admin/users/BulkUpload'));
const Clients = lazy(() => import('../pages/admin/clients'));
const AddEditClient = lazy(() => import('../pages/admin/clients/AddEditClient'));
const Admins = lazy(() => import('../pages/admin/admins'));
const AddEditAdmin = lazy(() => import('../pages/admin/admins/AddEditAdmin'));
const Sources = lazy(() => import('../pages/admin/sources'));
const AddEditSources = lazy(() => import('../pages/admin/sources/AddEditSources'));
const OrderList = lazy(() => import('../pages/order/orderList'));
const ViewFormDetailsUpdated = lazy(() => import('../pages/admin/participantForms/ViewFormDetailsUpdated'));
const UserProfile = lazy(() => import('../pages/common/UserProfile'));
const ChangePassword = lazy(() => import('../pages/common/ChangePassword'));
const AddOrder = lazy(() => import('../pages/order/addOrder'));
const ResetPassword = lazy(() => import('../pages/common/ResetPassword'));
const ViewClient = lazy(() => import('../pages/admin/clients/ViewClient'));
const ViewUser = lazy(() => import('../pages/admin/users/ViewUser'));
const ViewAdmin = lazy(() => import('../pages/admin/admins/ViewAdmin'));
const ViewSource = lazy(() => import('../pages/admin/sources/ViewSource'));
const ForgotPassword = lazy(() => import('../pages/common/ForgotPassword'));
const ResetForgotPassword = lazy(() => import('../pages/common/ResetForgotPassword'));
const OptAuth = lazy(() => import('../pages/admin/admins/OptAuth'));
const Configurations = lazy(() => import('../pages/admin/configurations'));
const BrandingAndMessaging = lazy(() => import('../pages/admin/branding'));
const Offices = lazy(() => import('../pages/admin/clients/Offices'));
const Office = lazy(() => import('../pages/admin/clients/Office'));
const AuditTrails = lazy(() => import('../pages/admin/auditTrails'));
const DocumentConfigurations = lazy(() => import('../pages/admin/configurations/DocumentConfigurations'));
const Documentation = lazy(() => import('../pages/admin/documentation'));
const Reports = lazy(() => import('../pages/admin/reports'));
const MobySuccess = lazy(() => import('../pages/common/MobySuccess'));
const WindowClose = lazy(() => import('../pages/common/WindowClose'));
const BulkUpload = lazy(() => import('../pages/order/addOrder/BulkUpload'));
const OrderDetailsUpdated = lazy(() => import('../pages/order/orderDetail/OrderDetailsNewLayout'));
const ImportedOrderList = lazy(() => import('../pages/order/orderList/ImportedOrderList'));
const IntegrationLogs = lazy(() => import('../pages/admin/integrationLogs'));
const OrderTypes = lazy(() => import('../pages/admin/configurations/OrderTypes'));
const IntegrationFailureManagement = lazy(() => import('../pages/admin/intgerationFailureManagement'));
const RxOrderList = lazy(() => import('../pages/rx/orderList'));
const RxAddIdOnly = lazy(() => import('../pages/rx/addIdOnly'));
const RxOrderDetail = lazy(() => import('../pages/rx/orderDetail'));
const RxAddQuestionnaire = lazy(() => import('../pages/rx/addQuestionnaire'));
const RxAddFull = lazy(() => import('../pages/rx/addFull'));
const RequestsQueue = lazy(() => import('../pages/order/requests'));

const Routes = () => {
  const routes = useRoutes([
    {
      element: (
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
      ),
      children: [
        {
          path: '/',
          element: <Navigate to='/login' />,
        },
        { path: 'participant/auth/:code', element: <OptAuth /> },
        { path: 'participant/dl/confirmation/:code', element: <MobySuccess /> },
        { path: 'window-close', element: <WindowClose /> },
        {
          path: '/reset-password',
          element: <ResetPassword />,
        },
        {
          path: '/forgot-password',
          children: [
            { path: '', element: <ForgotPassword /> },
            {
              path: 'reset/:token',
              element: <ResetForgotPassword />,
            },
          ],
        },
        {
          path: '/questions',
          children: [
            { path: 'invite/:code', element: <Invite /> },
            { path: 'invite/:code/:participantNo', element: <Invite /> },
            {
              path: 'home/:code',
              element: <PrivateRoute component={<Home />} type='participant' />,
            },
            {
              path: 'home/:code/:participantNo',
              element: <PrivateRoute component={<Home />} type='participant' />,
            },
            { path: 'invite/:code/:participantNo/:memberUUID', element: <Invite /> },
            {
              path: 'home/:code/:participantNo/:memberUUID',
              element: <PrivateRoute component={<Home />} type='participant' />,
            },
          ],
        },
        { path: 'login', element: <Login /> },
        { path: '/:office/login', element: <Login /> },
        { path: '/offices', element: <Offices /> },
        { path: '/:office', element: <Office /> },
        {
          path: '/admin',
          element: (
            <MainLayout>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </MainLayout>
          ),
          children: [
            { path: '', element: <Navigate to={'/admin/dashboard'} /> },
            {
              path: 'audit-trails',
              element: (
                <PrivateRoute
                  component={
                    <AuditTrails />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Audit Trails',
                      url: '/admin/audit-trails',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'clients/:user_id/:id',
              element: (
                <PrivateRoute
                  component={
                    <AddEditClient />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: ':id',
                      url: '/admin/:user_id/:id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'configurations',
              element: (
                <PrivateRoute
                  component={
                    <Configurations />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Preferences',
                      url: '/admin/configurations',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'branding',
              element: (
                <PrivateRoute
                  component={
                    <BrandingAndMessaging />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Branding and Messaging',
                      url: '/admin/branding',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'order-types',
              element: (
                <PrivateRoute
                  component={
                    <OrderTypes />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Order Setup',
                      url: '/admin/order-types',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'document-configurations',
              element: (
                <PrivateRoute
                  component={
                    <DocumentConfigurations />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Document Configurations',
                      url: '/admin/document-configurations',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'documentation',
              element: (
                <PrivateRoute
                  component={
                    <Documentation />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Help & FAQs',
                      url: '/admin/documentation',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'dashboard',
              element: (
                <PrivateRoute
                  component={
                    <Dashboard />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Dashboard',
                      url: '/admin/dashboard',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'reports',
              element: (
                <PrivateRoute
                  type='admin'
                  component={
                    <Reports />
                  }
                  breadcrumb={[
                    {
                      title: 'Reports',
                      url: '/admin/reports',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'integration-failure-management',
              element: (
                <PrivateRoute
                  type='admin'
                  component={
                    <IntegrationFailureManagement />
                  }
                  breadcrumb={[
                    {
                      title: 'Integration Failure Management',
                      url: '/admin/integration-failure-management',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'integration-logs',
              element: (
                <PrivateRoute
                  onlySuperAdminAccess={true}
                  type='admin'
                  component={
                    <IntegrationLogs />
                  }
                  breadcrumb={[
                    {
                      title: 'Integration Logs',
                      url: '/admin/integration-logs',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'templates',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <Templates />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      component={
                        <TemplateForm />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates',
                        },
                        {
                          title: ':id',
                          url: '/admin/templates/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/preview',
                  element: (
                    <PrivateRoute
                      component={
                        <TemplateFormPreviewPage />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates',
                        },
                        {
                          title: ':id',
                          url: '/admin/templates/:id/preview',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'users',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <Users />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Users',
                          url: '/admin/users',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      component={
                        <AddEditUser />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Users',
                          url: '/admin/users',
                        },
                        {
                          title: 'Add User',
                          url: '/orders/user/addUser',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'bulk-upload',
                  element: (
                    <PrivateRoute
                      component={
                        <BulkUploadUsers />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Users',
                          url: '/admin/users',
                        },
                        {
                          title: 'Bulk Upload',
                          url: '',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      component={
                        <AddEditUser />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Users',
                          url: '/admin/users',
                        },
                        {
                          title: ':id',
                          url: '/admin/users/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/view',
                  element: (
                    <PrivateRoute
                      component={
                        <ViewUser />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Users',
                          url: '/admin/users',
                        },
                        {
                          title: 'view',
                          url: '/admin/users/:id/view',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'clients',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <Clients />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Clients',
                          url: '/admin/clients',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <AddEditClient />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Clients',
                          url: '/admin/clients',
                        },
                        {
                          title: 'Add Client',
                          url: '/admin/clients/add',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <AddEditClient />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Clients',
                          url: '/admin/clients',
                        },
                        {
                          title: ':id',
                          url: '/admin/clients/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/view',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <ViewClient />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Client',
                          url: '/admin/clients',
                        },
                        {
                          title: 'view',
                          url: '/admin/clients/:id',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'templates/client/:clientId',
              element: (
                <PrivateRoute
                  component={
                    <Templates />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Form Setup',
                      url: '/admin/templates/client/:clientId',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'order-types/:client_id',
              element: (
                <PrivateRoute
                  component={
                    <OrderTypes />
                  }
                  type='admin'
                  clientLevelAccess={true}
                  breadcrumb={[
                    {
                      title: 'Order Setup',
                      url: '/admin/order-types/:client_id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'document-configurations/:client_id',
              element: (
                <PrivateRoute
                  component={
                    <DocumentConfigurations />
                  }
                  type='admin'
                  clientLevelAccess={true}
                  breadcrumb={[
                    {
                      title: 'Document Configurations',
                      url: '/admin/document-configurations/:client_id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'templates/client/:clientId/:id',
              element: (
                <PrivateRoute
                  component={
                    <TemplateForm />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Form Setup',
                      url: '/admin/templates/client/:clientId',
                    },
                    {
                      title: 'Template',
                      url: '/admin/templates/client/:clientId/:id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'templates/client/:clientId/:id/preview',
              element: (
                <PrivateRoute
                  component={
                    <TemplateFormPreviewPage />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Form Setup',
                      url: '/admin/templates/client/:clientId',
                    },
                    {
                      title: 'Template',
                      url: '/admin/templates/client/:clientId/:id/preview',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'configurations/:client_id',
              element: (
                <PrivateRoute
                  component={
                    <Configurations />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Client Preference',
                      url: '/admin/configurations/:client_id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'branding/:client_id',
              element: (
                <PrivateRoute
                  component={
                    <BrandingAndMessaging />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Branding and Messaging',
                      url: '/admin/branding/:client_id',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'list',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      onlySuperAdminAccess={true}
                      component={
                        <Admins />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Admins',
                          url: '/admin/list',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      onlySuperAdminAccess={true}
                      component={
                        <AddEditAdmin />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Admins',
                          url: '/admin/list',
                        },
                        {
                          title: 'Add Admin',
                          url: '/admin/list/add',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      onlySuperAdminAccess={true}
                      component={
                        <AddEditAdmin />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Admins',
                          url: '/admin/list',
                        },
                        {
                          title: ':id',
                          url: '/admin/admin/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/view',
                  element: (
                    <PrivateRoute
                      onlySuperAdminAccess={true}
                      component={
                        <ViewAdmin />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Admins',
                          url: '/admin/list',
                        },
                        {
                          title: 'view',
                          url: '/admin/admin/:id/view',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'sources',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <Sources />
                      }
                      type='admin'
                      onlyAdminAccess={true}
                      breadcrumb={[
                        {
                          title: 'Source Mapping',
                          url: '/admin/sources',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <AddEditSources />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Source Mapping',
                          url: '/admin/sources',
                        },
                        {
                          title: 'Add',
                          url: '/orders/sources/add',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <AddEditSources />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Source Mapping',
                          url: '/admin/sources',
                        },
                        {
                          title: ':id',
                          url: '/admin/sources/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/view',

                  element: (
                    <PrivateRoute
                      onlyAdminAccess={true}
                      component={
                        <ViewSource />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Source Mapping',
                          url: '/admin/sources',
                        },
                        {
                          title: 'view',
                          url: '/admin/sources/:id/view',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'imported-orders',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <ImportedOrderList />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/admin/imported-orders',
                        },
                      ]}
                    />
                  ),
                },

                {
                  path: 'bulk-upload',
                  element: (
                    <PrivateRoute
                      component={
                        <BulkUpload />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/admin/imported-orders',
                        },
                        {
                          title: 'Bulk Order Upload',
                          url: '/admin/imported-orders/bulk-upload',
                        },
                      ]}
                    />
                  ),
                },

                {
                  path: ':id/edit',
                  element: (
                    <PrivateRoute
                      component={
                        <AddOrder />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/admin/imported-orders',
                        },
                        {
                          title: 'Edit Order',
                          url: '/admin/imported-orders/:id/edit',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      component={
                        <OrderDetailsUpdated />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/admin/imported-orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/admin/imported-orders/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/:participantId',
                  element: (
                    <PrivateRoute
                      component={
                        <ViewFormDetailsUpdated />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/admin/imported-orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/admin/imported-orders/:id',
                        },
                        {
                          title: 'Participant Form Details',
                          url: '/admin/imported-orders/:participantId',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'orders',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <OrderList />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                      ]}
                    />
                  ),
                },

                {
                  path: 'bulk-upload',
                  element: (
                    <PrivateRoute
                      component={
                        <BulkUpload />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                        {
                          title: 'Bulk Order Upload',
                          url: '/admin/orders/bulk-upload',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      component={
                        <AddOrder />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                        {
                          title: 'Add Order',
                          url: '/admin/orders/add',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <PrivateRoute
                      component={
                        <AddOrder />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                        {
                          title: 'Edit Order',
                          url: '/admin/orders/:id/edit',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      component={
                        <OrderDetailsUpdated />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/admin/orders/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/:participantId',
                  element: (
                    <PrivateRoute
                      component={
                        <ViewFormDetailsUpdated />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Orders',
                          url: '/admin/orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/admin/orders/:id',
                        },
                        {
                          title: 'Participant Form Details',
                          url: '/admin/orders/:participantId',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            {
              path: 'profile',
              element: (
                <PrivateRoute
                  component={
                    <UserProfile />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Profile',
                      url: 'profile',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'ChangePassword',
              element: (
                <PrivateRoute
                  component={
                    <ChangePassword />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Change Password',
                      url: 'ChangePassword',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'configurations/master',
              element: (
                <PrivateRoute
                  component={
                    <Configurations />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Preferences',
                      url: '/admin/configurations/master',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'order-types/master',
              element: (
                <PrivateRoute
                  component={
                    <OrderTypes />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Order Setup',
                      url: '/admin/order-types/master',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'document-configurations/master',
              element: (
                <PrivateRoute
                  component={
                    <DocumentConfigurations />
                  }
                  type='admin'
                  breadcrumb={[
                    {
                      title: 'Document Configurations',
                      url: '/admin/document-configurations/master',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'templates/master',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      component={
                        <Templates />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates/master',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      component={
                        <TemplateForm />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates/master',
                        },
                        {
                          title: ':id',
                          url: '/admin/templates/master/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/preview',
                  element: (
                    <PrivateRoute
                      component={
                        <TemplateFormPreviewPage />
                      }
                      type='admin'
                      breadcrumb={[
                        {
                          title: 'Form Setup',
                          url: '/admin/templates/master',
                        },
                        {
                          title: ':id',
                          url: '/admin/templates/master/:id/preview',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
          ],
        },
        {
          path: '/orders',
          element: (
            <OrderMainLayout>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </OrderMainLayout>
          ),
          children: [
            {
              path: 'requests',
              element: (
                <PrivateRoute
                  onlyUserAccess={true}
                  component={<RequestsQueue />}
                  type='order'
                  breadcrumb={[{ title: 'Open requests', url: '/orders/requests' }]}
                />
              ),
            },
            {
              path: 'imported-orders',
              children: [
                {
                  path: '',
                  element: (
                    <PrivateRoute
                      onlyUserAccess={true}
                      component={
                        <ImportedOrderList />
                      }
                      type='order'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/Orders/imported-orders',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <PrivateRoute
                      onlyUserAccess={true}
                      component={
                        <OrderDetailsUpdated />
                      }
                      type='order'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/orders/imported-orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/orders/imported-orders/:id',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: ':id/:participantId',
                  element: (
                    <PrivateRoute
                      component={
                        <ViewFormDetailsUpdated />
                      }
                      type='order'
                      onlyUserAccess={true}
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/orders/imported-orders',
                        },
                        {
                          title: 'Order Summary',
                          url: '/orders/imported-orders/:id',
                        },
                        {
                          title: 'Participant Form Details',
                          url: '/orders/imported-orders/:id/:participantId',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'bulk-upload',
                  element: (
                    <PrivateRoute
                      component={
                        <BulkUpload />
                      }
                      type='order'
                      onlyUserAccess={true}
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/orders/imported-orders',
                        },
                        {
                          title: 'Bulk Order Upload',
                          url: '/orders/imported-orders/bulk-upload',
                        },
                      ]}
                    />
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <PrivateRoute
                      onlyUserAccess={true}
                      component={
                        <AddOrder />
                      }
                      type='order'
                      breadcrumb={[
                        {
                          title: 'Imported Orders',
                          url: '/orders/imported-orders',
                        },
                        {
                          title: 'Create Order',
                          url: '/orders/imported-orders/add',
                        },
                      ]}
                    />
                  ),
                },
              ],
            },
            { path: '', element: <Navigate to={'/orders/order'} /> },
            {
              path: 'reports',
              element: (
                <PrivateRoute
                  component={
                    <Reports />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Reports',
                      url: '/orders/reports',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'order',
              element: (
                <PrivateRoute
                  onlyUserAccess={true}
                  component={
                    <OrderList />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Orders',
                      url: '/orders/order',
                    },
                  ]}
                />
              ),
            },
            {
              path: ':id',
              element: (
                <PrivateRoute
                  onlyUserAccess={true}
                  component={
                    <OrderDetailsUpdated />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Orders',
                      url: '/orders/order',
                    },
                    {
                      title: 'Order Summary',
                      url: '/orders/:id',
                    },
                  ]}
                />
              ),
            },
            {
              path: ':id/:participantId',
              element: (
                <PrivateRoute
                  component={
                    <ViewFormDetailsUpdated />
                  }
                  type='order'
                  onlyUserAccess={true}
                  breadcrumb={[
                    {
                      title: 'Orders',
                      url: '/orders/order',
                    },
                    {
                      title: 'Order Details',
                      url: '/orders/:id',
                    },
                    {
                      title: 'Participant Form Details',
                      url: '/orders/:id/:participantId',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'bulk-upload',
              element: (
                <PrivateRoute
                  component={
                    <BulkUpload />
                  }
                  type='order'
                  onlyUserAccess={true}
                  breadcrumb={[
                    {
                      title: 'Orders',
                      url: '/orders',
                    },
                    {
                      title: 'Bulk Order Upload',
                      url: '/orders/bulk-upload',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'add',
              element: (
                <PrivateRoute
                  onlyUserAccess={true}
                  component={
                    <AddOrder />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Orders',
                      url: '/orders/order',
                    },
                    {
                      title: 'Create Order',
                      url: '/orders/add',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'profile',
              element: (
                <PrivateRoute
                  component={
                    <UserProfile />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Profile',
                      url: 'profile',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'ChangePassword',
              element: (
                <PrivateRoute
                  component={
                    <ChangePassword />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Change Password',
                      url: 'ChangePassword',
                    },
                  ]}
                />
              ),
            },
            {
              path: 'documentation',
              element: (
                <PrivateRoute
                  component={
                    <Documentation />
                  }
                  type='order'
                  breadcrumb={[
                    {
                      title: 'Help & FAQs',
                      url: '/orders/documentation',
                    },
                  ]}
                />
              ),
            },
          ],
        },

        {
          path: '/rx',
          element: (
            <RxMainLayout>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </RxMainLayout>
          ),
          children: [
            { path: '', element: <Navigate to='/rx/orders' /> },
            {
              path: 'orders',
              element: (
                <PrivateRoute
                  component={<RxOrderList />}
                  type='rx'
                  breadcrumb={[{ title: 'Orders', url: '/rx/orders' }]}
                />
              ),
            },
            {
              path: 'orders/new',
              element: (
                <PrivateRoute
                  component={<RxAddFull />}
                  type='rx'
                  breadcrumb={[
                    { title: 'Orders', url: '/rx/orders' },
                    { title: 'New transaction', url: '/rx/orders/new' },
                  ]}
                />
              ),
            },
            {
              path: 'orders/id-only',
              element: (
                <PrivateRoute
                  component={<RxAddIdOnly />}
                  type='rx'
                  breadcrumb={[
                    { title: 'Orders', url: '/rx/orders' },
                    { title: 'New ID Only', url: '/rx/orders/id-only' },
                  ]}
                />
              ),
            },
            {
              path: 'orders/:id',
              element: (
                <PrivateRoute
                  component={<RxOrderDetail />}
                  type='rx'
                  breadcrumb={[
                    { title: 'Orders', url: '/rx/orders' },
                    { title: 'Order', url: '/rx/orders/:id' },
                  ]}
                />
              ),
            },
            {
              path: 'orders/:id/add-questionnaire',
              element: (
                <PrivateRoute
                  component={<RxAddQuestionnaire />}
                  type='rx'
                  breadcrumb={[
                    { title: 'Orders', url: '/rx/orders' },
                    { title: 'Order', url: '/rx/orders/:id' },
                    { title: 'Add Questionnaire', url: '/rx/orders/:id/add-questionnaire' },
                  ]}
                />
              ),
            },
          ],
        },
        { path: '*', element: <Error404Page /> },
      ],
    },
  ]);
  return routes;
};

export default Routes;
